import { useState } from 'react';
import { ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import type { AdminRow, ProvisionedCredentials } from '@nlinks/shared';
import { ApiError } from '@/lib/api';
import { useAdmins, useSuperMutations } from '@/lib/queries';
import { formatRelative } from '@/lib/format';
import { CredentialsDialog } from '@/components/CredentialsDialog';
import { InfoButton } from '@/components/InfoButton';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Modal,
  Spinner,
  useToast,
} from '@/components/ui';

export function SuperAdminsPage() {
  const { data: admins, isLoading } = useAdmins();
  const { createAdmin, deleteAdmin } = useSuperMutations();
  const { push } = useToast();

  const [adding, setAdding] = useState(false);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ credentials: ProvisionedCredentials; fullName: string } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<AdminRow | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await createAdmin.mutateAsync({ fullName: fullName.trim(), email: email.trim() });
      setCreated({ credentials: result.credentials, fullName: result.admin.fullName });
      setFullName('');
      setEmail('');
      setAdding(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create this administrator.');
    }
  };

  const remove = async () => {
    if (!confirmDelete) return;
    try {
      await deleteAdmin.mutateAsync(confirmDelete.id);
      push('success', `${confirmDelete.fullName} removed.`);
    } catch (err) {
      push('error', err instanceof ApiError ? err.message : 'Could not remove that administrator.');
    } finally {
      setConfirmDelete(null);
    }
  };

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">Administrators</h1>
          <p className="mt-1 text-sm text-slate-500">
            Admins can create and manage users. Only you can add or remove them.
          </p>
        </div>
        <Button onClick={() => setAdding(true)} icon={<UserPlus className="h-4 w-4" />}>
          Add administrator
        </Button>
      </div>

      <Card>
        <CardHeader title="All administrators" description={`${admins?.length ?? 0} account(s)`} />
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : (
          <ul className="divide-y divide-surface-border">
            {admins?.map((admin) => {
              const isSuper = admin.status === 'SUPER_ADMIN';
              return (
                <li key={admin.id} className="flex flex-wrap items-center gap-4 px-5 py-4">
                  <div
                    className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                      isSuper ? 'bg-amber-500/10 text-amber-400' : 'bg-brand-500/10 text-brand-400'
                    }`}
                  >
                    <ShieldCheck className="h-4 w-4" aria-hidden />
                  </div>

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="font-medium text-slate-200">{admin.fullName}</p>
                      {isSuper ? <Badge tone="warning">Super admin</Badge> : null}
                    </div>
                    <p className="truncate text-xs text-slate-500">{admin.email}</p>
                  </div>

                  <div className="text-right text-xs">
                    <p className="flex items-center justify-end gap-1.5 text-slate-400">
                      {admin.usersCreated} user{admin.usersCreated === 1 ? '' : 's'} created
                      <InfoButton label="Users created" align="right">
                        Accounts this administrator provisioned. Removing the administrator does not affect them.
                      </InfoButton>
                    </p>
                    <p className="text-slate-600">signed in {formatRelative(admin.lastLoginAt)}</p>
                  </div>

                  {/* The super admin is the root of the permission tree — removing
                      it would leave nobody able to appoint administrators. */}
                  {isSuper ? (
                    <span className="w-8 text-center text-xs text-slate-700" title="The super admin cannot be removed">
                      —
                    </span>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(admin)}
                      className="rounded-lg p-1.5 text-slate-500 transition hover:bg-red-500/10 hover:text-red-400"
                      aria-label={`Remove ${admin.fullName}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      <Modal
        open={adding}
        onClose={() => setAdding(false)}
        title="Add an administrator"
        description="A password is generated and shown once."
      >
        <form onSubmit={submit} className="space-y-5">
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Field label="Full name" required htmlFor="adminName">
            <Input
              id="adminName"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
              minLength={2}
              autoFocus
            />
          </Field>

          <Field label="Email address" required htmlFor="adminEmail" hint="This becomes their login ID.">
            <Input id="adminEmail" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </Field>

          <div className="flex items-center justify-end gap-3 border-t border-surface-border pt-5">
            <Button type="button" variant="ghost" onClick={() => setAdding(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={createAdmin.isPending}>
              Create administrator
            </Button>
          </div>
        </form>
      </Modal>

      {created ? (
        <CredentialsDialog
          email={created.credentials.email}
          password={created.credentials.temporaryPassword}
          fullName={created.fullName}
          onClose={() => setCreated(null)}
        />
      ) : null}

      <Modal
        open={Boolean(confirmDelete)}
        onClose={() => setConfirmDelete(null)}
        title="Remove this administrator?"
        size="sm"
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={remove} loading={deleteAdmin.isPending}>
              Remove
            </Button>
          </>
        }
      >
        <p className="text-sm text-slate-400">
          <span className="font-medium text-slate-200">{confirmDelete?.fullName}</span> will lose access immediately.
          Users they created are unaffected.
        </p>
      </Modal>
    </div>
  );
}
